import { useState } from 'react';
import { Box } from '@mui/material';
import {
  Breadcrumb,
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  DensityToggle,
  FormSearchResultAdvanced,
  generateLocaleId,
  useDataProvider,
  useLocalization,
  useStore,
} from '@ui-framework/index';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';
import {
  serviceJobNumber,
  alertReason,
  alertTime,
  appointmentDate,
  customerAddressCity,
  customerAddressState,
  productType,
  brand,
  customerComplaint,
} from './accessorFunctions';

import listSchema from './schemas/list.json';
import searchSchema from './schemas/search.json';
import validationSchema from './validations';

function ListHeaderContent({ setTableDensity }) {
  return (
    <DensityToggle
      sx={{ display: 'none' }}
      onDensityChange={args => setTableDensity(args)}
    />
  );
}

function ServiceJobAlertList() {
  const defaultListTitle = {
    id: 'SERVICE_JOB_ALERTS',
    defaultMessage: 'Job Alerts',
  };
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(0);
  const [tableDensity, setTableDensity] = useState('comfortable');
  const store = useStore();
  const [searchUrlParams] = useSearchParams();
  const searchTitle = searchUrlParams.get('searchTitle') || '';

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/insights/communication-alerts`;

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'LIST_JOB_ALERTS',
        defaultMessage: 'List Job Alerts',
      }),
      icon: IconLibrary.Alerts,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: !showSearch
        ? formatMessage({
            id: 'SHOW_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Show Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Hide Search',
          }),
      Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      permissionName: 'SEARCH_SERVICE_JOB_ALERT',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data => {
    const { id: idArg, ...other } = data;
    return Object.fromEntries(
      Object.entries({
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set('ServiceJobAlertList', searchState);
  };

  const preProcessSearchValues = searchedParams => {
    return searchedParams;
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'ServiceJobAlertList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      serviceJobNumber,
      alertReason,
      alertTime,
      appointmentDate,
      customerAddressCity,
      customerAddressState,
      productType,
      brand,
      customerComplaint,
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    sortOrder: 'DESC',
    sortBy: 'alertTime',
  };

  const searchProps = {
    customComponents: FormFields,
    storeName: 'ServiceJobAlertList',
    IconLibrary,
    schema: searchSchema,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset('ServiceJobAlertList');
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    tableDensity,
    initialValues: {},
    getValueByControl: getDefaultValueByControl,
  };

  const searchParamsProps = {
    preProcessSearchValues,
    storeName: 'ServiceJobAlertList',
    schema: searchSchema,
    resultsFound,
  };

  const getPageTitle = () => {
    const currentStore = store.domain.list.get('ServiceJobAlertList');
    if (searchTitle && currentStore?.filters?.length > 0) {
      return {
        id: generateLocaleId(searchTitle),
        defaultMessage: searchTitle,
      };
    }
    return defaultListTitle;
  };

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <Grid2 xs={12}>
        <FormSearchResultAdvanced {...searchParamsProps} />
      </Grid2>

      {showSearch && (
        <PageSection
          containerProps={{ sx: { mb: 3 } }}
          label={formatMessage({
            id: 'SEARCH_JOB_ALERTS',
            defaultMessage: 'Search Job Alerts',
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
              searchProps={searchProps}
            />
          </Box>
        </PageSection>
      )}

      <PageSection
        HeaderContent={<ListHeaderContent setTableDensity={setTableDensity} />}
        label={formatMessage(getPageTitle())}>
        <Box>
          <CRUDComponent
            type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
            listProps={listProps}
          />
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(ServiceJobAlertList);
